import type { ReactElement } from 'react';
import {
  createActionsColumn,
  createSerialColumn,
  DrillName,
  StackedCellText,
} from '@/components/common/DataTable';
import { RowActionButton, RowActionGroup, RowViewRender } from '@/components/common/RowActions';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatDisplayDate } from '@/utils/date';
import { OPTION_DETAIL_SEPARATOR } from '@/utils/filters';
import { formatNumber } from '@/utils/number';
import { BatchStageBadge } from './BatchStageBadge';
import type { Batch, BatchMember } from '../types/publish.types';
import { formatBatchAmount } from '../utils/formatBatchAmount';
import type { ColumnDef } from '@tanstack/react-table';

export interface BatchRowAction {
  label: string;
  onClick: (batch: Batch) => void;
  when: (batch: Batch) => boolean;
}

export function getBatchColumns(
  getMembersLink: (batch: Batch) => ReactElement,
  action?: BatchRowAction,
): ColumnDef<Batch, unknown>[] {
  return [
    createSerialColumn<Batch>(),
    {
      id: 'batch',
      header: 'Batch',
      cell: ({ row }) => (
        <DrillName
          value={`#${String(row.original.legacyId)}`}
          label={`Members of batch ${String(row.original.legacyId)}`}
          render={getMembersLink(row.original)}
        />
      ),
    },
    {
      id: 'window',
      header: 'Covering',
      cell: ({ row }) =>
        row.original.trnDateFrom === null
          ? '—'
          : `${formatDisplayDate(row.original.trnDateFrom)} – ${formatDisplayDate(row.original.trnDateTo)}`,
    },
    {
      id: 'published',
      header: 'Published',
      cell: ({ row }) => (
        <StackedCellText
          primary={formatDisplayDate(row.original.publishDate)}
          secondary={row.original.publishedBy}
        />
      ),
    },
    {
      id: 'dcr',
      header: 'DCR',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatBatchAmount(row.original.dcrAmount)}</span>
      ),
    },
    {
      id: 'der',
      header: 'DER',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatBatchAmount(row.original.derAmount)}</span>
      ),
    },
    {
      id: 'total',
      header: 'Total',
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {formatBatchAmount(row.original.totalAmount)}
        </span>
      ),
    },
    {
      id: 'members',
      header: 'ZMs',
      cell: ({ row }) =>
        row.original.rowsArchived ? (
          <span className="text-xs text-muted-foreground">archived</span>
        ) : (
          <span className="tabular-nums">{formatNumber(row.original.memberCount)}</span>
        ),
    },
    {
      id: 'stage',
      header: 'Stage',
      cell: ({ row }) => <BatchStageBadge stage={row.original.stage} />,
    },
    createActionsColumn<Batch>((batch) => (
      <RowActionGroup>
        {action?.when(batch) === true ? (
          <RowActionButton
            label={action.label}
            ariaLabel={`${action.label} batch ${batch.legacyId}`}
            onClick={() => action.onClick(batch)}
          />
        ) : null}
        <RowViewRender
          render={getMembersLink(batch)}
          label="Members"
          ariaLabel={`Members of batch ${String(batch.legacyId)}`}
        />
      </RowActionGroup>
    )),
  ];
}

export function getBatchMemberColumns(): ColumnDef<BatchMember, unknown>[] {
  return [
    createSerialColumn<BatchMember>(),
    {
      id: 'member',
      header: 'ZM Name',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.fullName ?? EMPTY_VALUE}
          primaryClassName="font-medium"
          secondary={[row.original.hrEmpId, row.original.designationName]
            .filter(Boolean)
            .join(OPTION_DETAIL_SEPARATOR)}
        />
      ),
    },
    {
      id: 'where',
      header: 'Thana, District',
      cell: ({ row }) =>
        [row.original.thanaName, row.original.districtName].filter(Boolean).join(', ') ||
        EMPTY_VALUE,
    },
    {
      id: 'dcr',
      header: 'DCR',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatBatchAmount(row.original.dcrAmount)}</span>
      ),
    },
    {
      id: 'der',
      header: 'DER',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatBatchAmount(row.original.derAmount)}</span>
      ),
    },
    {
      id: 'total',
      header: 'Total',
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {formatBatchAmount(row.original.totalAmount)}
        </span>
      ),
    },
  ];
}
